import { useMemo } from 'react';

import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from 'api/constants';
import { EOverrideAccessRights } from 'api/projectTeamConfigurations/client-projectTeamApi';
import { ProjectTeamMappingsApi } from 'api/projectTeamMappings/client-projectTeamMappings';

import { PROJECT_TEAM_MAPPINGS } from './projectTeamMappingState';

export const useProjectTeamMappingsActions = () => {
  const queryClient = useQueryClient();
  return useMemo(() => new ProjectTeamMappingsActions(queryClient), [queryClient]);
};

export class ProjectTeamMappingsActions {
  api: ProjectTeamMappingsApi;
  private queryClient: QueryClient;

  constructor(queryClient: QueryClient) {
    this.api = new ProjectTeamMappingsApi();
    this.queryClient = queryClient;
  }

  async GetAll() {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.GetAll();

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().loadAll(result.result);
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
  }

  async CreateMapping(projectTeamId: any, templateId: string) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.CreateProjectTeamMapping(projectTeamId, templateId);

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
      this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROJECT_TEAMS] });
    }
    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async UpdateAccessRights(
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    moduleId: string,
    accessRights: EOverrideAccessRights
  ) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.UpdateAccessRights(
      projectTeamId,
      projectTeamMappingId,
      projectTeamRoleId,
      moduleId,
      accessRights
    );

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
    }
    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async RemoveModuleAssignment(
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    moduleId: string
  ) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.RemoveModuleAssignment(
      projectTeamId,
      projectTeamMappingId,
      projectTeamRoleId,
      moduleId
    );

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
    }
    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async AddPropertyOverrideToRole(
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    propertyId: string,
    accessRights?: EOverrideAccessRights
  ) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.AddPropertyOverrideToRole(
      projectTeamId,
      projectTeamMappingId,
      projectTeamRoleId,
      propertyId,
      accessRights ?? EOverrideAccessRights.view
    );

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async ChangePropertyOverrideForRole(
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    propertyId: string,
    accessRights: EOverrideAccessRights
  ) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.ChangePropertyOverrideForRole(
      projectTeamId,
      projectTeamMappingId,
      projectTeamRoleId,
      propertyId,
      accessRights
    );

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async RemovePropertyOverrideFromRole(
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    propertyId: string
  ) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.RemovePropertyOverrideFromRole(
      projectTeamId,
      projectTeamMappingId,
      projectTeamRoleId,
      propertyId
    );

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async AddEdgeOverrideToRole(
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    relationId: string,
    accessRights?: EOverrideAccessRights
  ) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.AddEdgeOverrideToRole(
      projectTeamId,
      projectTeamMappingId,
      projectTeamRoleId,
      relationId,
      accessRights ?? EOverrideAccessRights.view
    );

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async ChangeEdgeOverrideForRole(
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    relationId: string,
    accessRights: EOverrideAccessRights
  ) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.ChangeEdgeOverrideForRole(
      projectTeamId,
      projectTeamMappingId,
      projectTeamRoleId,
      relationId,
      accessRights
    );

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async RemoveEdgeOverrideFromRole(
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    relationId: string
  ) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.RemoveEdgeOverrideFromRole(
      projectTeamId,
      projectTeamMappingId,
      projectTeamRoleId,
      relationId
    );

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);
    return result;
  }

  async DeleteProjectTeamMapping(projectTeamId: any, projectTeamMappingId: any) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.DeleteProjectTeamMapping(projectTeamId, projectTeamMappingId);

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().delete(result.result.projectTeamMappingId);
      this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROJECT_TEAMS] });
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);

    return result;
  }

  async MigrateProjectTeamMapping(projectTeamMappingId: any, targetTemplateId: string) {
    PROJECT_TEAM_MAPPINGS.getState().setLoading(true);
    const result = await this.api.MigrateProjectTeamMapping(projectTeamMappingId, targetTemplateId);

    if (result.success) {
      PROJECT_TEAM_MAPPINGS.getState().addOrUpdate(result.result);
      this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROJECT_TEAMS] });
    }

    PROJECT_TEAM_MAPPINGS.getState().setLoading(false);

    return result;
  }
}
